import React, { useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLicense } from "./LicenseContext";

/**
 * LicenseBanner
 * Displays a non-intrusive banner at the top of the app when:
 *  - Mode is "Community"  → info banner
 *  - Mode is "Expired" + grace → warning banner
 *  - Mode is "Expired" (no grace) / "Invalid" / "Revoked" / "Missing" → error banner
 *
 * Renders nothing when the license is fully Licensed and operational.
 */
export function LicenseBanner() {
  const { status, loading, mode } = useLicense();
  const { t } = useTranslation();
  const bannerRef = useRef(null);

  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (el) {
      document.documentElement.style.setProperty("--banner-height", el.offsetHeight + "px");
    } else {
      document.documentElement.style.setProperty("--banner-height", "0px");
    }
    return () => {
      document.documentElement.style.setProperty("--banner-height", "0px");
    };
  });

  if (loading || !status) return null;

  const getBannerConfig = () => {
    switch (mode) {
      case "Community":
        return {
          show: true,
          type: "info",
          message: t("license.banner.community", {
            maxUsers: status.maxUsers ?? "—",
            maxDocs: status.maxDocumentsPerYear ?? "—",
          }),
        };
      case "Expired":
        if (status.isInGrace) {
          return {
            show: true,
            type: "warning",
            message: t("license.banner.expiredGrace", {
              expiresAt: status.expiresAt
                ? new Date(status.expiresAt).toLocaleDateString()
                : "—",
            }),
          };
        }
        return {
          show: true,
          type: "error",
          message: t("license.banner.expired"),
        };
      case "Invalid":
        return {
          show: true,
          type: "error",
          message: t("license.banner.invalid"),
        };
      case "Revoked":
        return {
          show: true,
          type: "error",
          message: t("license.banner.revoked"),
        };
      case "Missing":
        return {
          show: true,
          type: "warning",
          message: t("license.banner.missing"),
        };
      default:
        return { show: false };
    }
  };

  const config = getBannerConfig();

  if (!config.show) return null;

  const colorMap = {
    info: { bg: "#e8f4fd", border: "#1890ff", text: "#0056b3", icon: "ℹ️" },
    warning: { bg: "#fff7e6", border: "#fa8c16", text: "#ad4e00", icon: "⚠️" },
    error: { bg: "#fff1f0", border: "#ff4d4f", text: "#a8071a", icon: "🚫" },
  };

  const colors = colorMap[config.type] ?? colorMap.info;

  return (
    <div
      ref={bannerRef}
      style={{
        backgroundColor: colors.bg,
        borderBottom: `2px solid ${colors.border}`,
        color: colors.text,
        padding: "8px 16px",
        fontSize: "13px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        zIndex: 9999,
      }}
      role="alert"
    >
      <span>{colors.icon}</span>
      <span>{config.message}</span>
      {mode === "Community" && (
        <span style={{ marginLeft: "auto", fontWeight: 600 }}>
          {t("license.banner.communityTier")}
        </span>
      )}
    </div>
  );
}
